import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AnalysisReport from './AnalysisReport';
import { catalystFetch } from '../services/catalystFetch';

/**
 * SessionDetail
 * Fetches a single session by ID and renders it via AnalysisReport.
 * Route: /history/:id
 */
const API_BASE = process.env.NODE_ENV === 'production'
  ? '/server/processMedia'
  : '';

const mutedText = { color: 'var(--text-secondary)' };
const errorText = { color: 'var(--color-danger)' };

export default function SessionDetail() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    catalystFetch(`${API_BASE}/sessions/${id}`, { method: 'GET' })
      .then(res => {
        if (res.status === 404) { setNotFound(true); setLoading(false); return null; }
        if (res.status === 403) { setForbidden(true); setLoading(false); return null; }
        if (!res.ok) throw new Error('fetch failed');
        return res.json();
      })
      .then(data => {
        if (!data) return;
        setSession(data.session);
        setLoading(false);
      })
      .catch((err) => {
        console.error('[SessionDetail] Fetch error:', err);
        setError(true);
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="text-center py-16 text-sm" style={mutedText}>
        Loading session…
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="text-center py-16 space-y-3">
        <p className="font-semibold" style={errorText}>Session not found.</p>
        <Link to="/analyze" className="text-sm" style={{ color: 'var(--accent)' }}>
          ← Back to Coaching
        </Link>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="text-center py-16 space-y-2">
        <p className="text-4xl mb-3">🔒</p>
        <p className="font-semibold" style={errorText}>Access denied</p>
        <p className="text-sm" style={mutedText}>You do not have permission to access this session.</p>
        <Link to="/analyze" className="inline-block mt-2 text-sm" style={{ color: 'var(--accent)' }}>
          ← Back to Coaching
        </Link>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="text-center py-16 space-y-3">
        <p className="font-semibold" style={errorText}>Failed to load session.</p>
        <Link to="/analyze" className="text-sm" style={{ color: 'var(--accent)' }}>
          ← Back to Coaching
        </Link>
      </div>
    );
  }

  const result = {
    session_id: session.session_id,
    transcript: session.transcript,
    language:   session.language,
    metrics:    session.metrics,
  };

  const downloadUrl = session.file_id
    ? `${API_BASE}/sessions/${session.session_id}/download`
    : null;

  return (
    <div>
      {downloadUrl && (
        <div
          className="rounded-xl p-4 mb-6"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}
        >
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>
            Recording
          </h3>
          {session.mime_type?.startsWith('video/') ? (
            <video controls className="w-full rounded-lg max-h-80" src={downloadUrl}>
              Your browser does not support the video element.
            </video>
          ) : (
            <audio controls className="w-full" src={downloadUrl}>
              Your browser does not support the audio element.
            </audio>
          )}
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs" style={mutedText}>
              {session.file_name || 'Recording'}
              {session.file_size_bytes && ` (${(session.file_size_bytes / 1024 / 1024).toFixed(1)} MB)`}
            </span>
            <a
              href={downloadUrl}
              download={session.file_name || 'recording'}
              className="text-sm font-medium"
              style={{ color: 'var(--accent)' }}
            >
              Download
            </a>
          </div>
        </div>
      )}
      <AnalysisReport result={result} onReset={() => window.history.back()} />
    </div>
  );
}
